import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SavingsIcon from "@mui/icons-material/Savings";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  CircularProgress,
} from "@mui/material";
import { debounce } from "lodash";
import MensagemSucesso from "../../components/MensagemSucesso";
import MensagemErro from "../../components/MensagemErro";
import {
  obterImpostosEsocial,
  cadastrarImpostosEsocial,
  atualizarImpostosEsocial,
  excluirImpostosEsocial,
  buscarTributariasParametros,
} from "../../services/api-relatorios.service";
import { listarEmpresas } from "../../services/api-empresas.service";
import {
  formatarCnpj,
  formatarPct,
  formatarPctInput,
  validarAliquotas,
  mmAaaaParaIso,
  isoParaMmAaaa,
  mascararMmAaaa,
  ordenarImpostos,
  filtrarImpostosClient,
} from "./impostosEsocial.util";
import styles from "./styles.module.css";

const LIMITE_BUSCA_EMPRESAS = 12;
const DEFAULTS = { terceiros: 5.8, rat: 2, cprb: "Não", inss: 20 };

function mapearLinhaApi(item) {
  return {
    idImposto: item.idImposto,
    cnpj: String(item.cnpj || "").replace(/\D/g, ""),
    razaoSocial: item.razaoSocial || "",
    referencia: String(item.referencia || "").slice(0, 7),
    referenciaDisplay: isoParaMmAaaa(item.referencia),
    terceiros: Number(item.terceiros),
    rat: Number(item.rat),
    inss: Number(item.inss),
    cprb: item.cprb === "Sim" ? "Sim" : "Não",
  };
}

function formatarOpcoesEmpresas(dados) {
  return (dados || [])
    .map((emp) => {
      const cnpj = String(emp.cnpj || "").replace(/\D/g, "");
      const razaoSocial =
        emp.razao_social || emp.razaoSocial || emp.nome || "";
      return {
        idEmpresa: emp.id_empresa ?? emp.id ?? null,
        cnpj,
        nome: razaoSocial,
      };
    })
    .filter((emp) => /^\d{14}$/.test(emp.cnpj) && emp.nome);
}

/**
 * Parametrizações — Impostos INSS (eSocial V3).
 * Layout: `definicao/esocial-imposto.txt`. Fonte: `public.impostos_esocial`.
 */
const Impostos = ({ data }) => {
  const [linhas, setLinhas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [openSnackSucesso, setOpenSnackSucesso] = useState(false);
  const [openSnackErro, setOpenSnackErro] = useState(false);

  const [filtroEmpresaTexto, setFiltroEmpresaTexto] = useState("");
  const [filtroEmpresaSel, setFiltroEmpresaSel] = useState(null);
  const [filtroSugestoes, setFiltroSugestoes] = useState([]);
  const [filtroSugVisible, setFiltroSugVisible] = useState(false);
  const [filtroRefIni, setFiltroRefIni] = useState("");
  const [filtroRefFim, setFiltroRefFim] = useState("");
  const [filtroCprb, setFiltroCprb] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [editId, setEditId] = useState(null);
  const [empresaModal, setEmpresaModal] = useState(null);
  const [modalBuscaEmpresa, setModalBuscaEmpresa] = useState("");
  const [modalSugestoes, setModalSugestoes] = useState([]);
  const [modalSugVisible, setModalSugVisible] = useState(false);
  const [periodoIni, setPeriodoIni] = useState("");
  const [periodoFim, setPeriodoFim] = useState("");
  const [referenciaEdit, setReferenciaEdit] = useState("");
  const [terceiros, setTerceiros] = useState("");
  const [rat, setRat] = useState("");
  const [cprb, setCprb] = useState("Não");
  const [inss, setInss] = useState("");
  const [modalErro, setModalErro] = useState("");
  const [taxa20sPadrao, setTaxa20sPadrao] = useState(DEFAULTS.terceiros);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const buscaAbortRef = useRef(null);
  const pageRef = useRef(null);

  const preFiltroCnpj = data?.cnpj
    ? String(data.cnpj).replace(/\D/g, "")
    : "";

  const carregarImpostos = useCallback(async () => {
    setLoading(true);
    try {
      const consulta = {};
      if (preFiltroCnpj && /^\d{14}$/.test(preFiltroCnpj)) {
        consulta.cnpj = preFiltroCnpj;
      } else if (data?.idEmpresa != null && Number(data.idEmpresa) > 0) {
        consulta.idEmpresa = Number(data.idEmpresa);
      }
      const response = await obterImpostosEsocial(consulta);
      const impostos = (response.impostos || []).map(mapearLinhaApi);
      setLinhas(ordenarImpostos(impostos));
    } catch (error) {
      setLinhas([]);
      setMsg(
        error.response?.data?.mensagem ||
          "Erro ao carregar impostos eSocial."
      );
      setOpenSnackErro(true);
    } finally {
      setLoading(false);
    }
  }, [preFiltroCnpj, data?.idEmpresa]);

  useEffect(() => {
    carregarImpostos();
  }, [carregarImpostos]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await buscarTributariasParametros();
        const lista = res?.parametros || res || [];
        const arr = Array.isArray(lista) ? lista : [];
        const taxa = arr.find((p) => p.chave === "taxa_20s");
        if (!cancelled && taxa != null && Number.isFinite(Number(taxa.valor))) {
          setTaxa20sPadrao(Number(taxa.valor));
        }
      } catch {
        /* default 5.8 */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const buscarEmpresasApi = useCallback(async (busca) => {
    if (buscaAbortRef.current) buscaAbortRef.current.abort();
    buscaAbortRef.current = new AbortController();
    try {
      const params = { limit: LIMITE_BUSCA_EMPRESAS, offset: 0 };
      if (busca != null && String(busca).trim() !== "") {
        params.q = String(busca).trim();
      }
      const response = await listarEmpresas(params);
      const dados =
        response?.dados != null
          ? response.dados
          : Array.isArray(response)
            ? response
            : [];
      return formatarOpcoesEmpresas(dados);
    } catch (err) {
      if (err?.name === "AbortError") return [];
      return [];
    } finally {
      buscaAbortRef.current = null;
    }
  }, []);

  const debouncedFiltroEmpresa = useMemo(
    () =>
      debounce(async (texto) => {
        if (!texto || String(texto).trim().length < 2) {
          setFiltroSugestoes([]);
          setFiltroSugVisible(false);
          return;
        }
        const lista = await buscarEmpresasApi(texto);
        setFiltroSugestoes(lista);
        setFiltroSugVisible(lista.length > 0);
      }, 300),
    [buscarEmpresasApi]
  );

  const debouncedModalEmpresa = useMemo(
    () =>
      debounce(async (texto) => {
        if (!texto || String(texto).trim().length < 2) {
          setModalSugestoes([]);
          setModalSugVisible(false);
          return;
        }
        const lista = await buscarEmpresasApi(texto);
        setModalSugestoes(lista);
        setModalSugVisible(lista.length > 0);
      }, 300),
    [buscarEmpresasApi]
  );

  useEffect(() => {
    return () => {
      debouncedFiltroEmpresa.cancel();
      debouncedModalEmpresa.cancel();
    };
  }, [debouncedFiltroEmpresa, debouncedModalEmpresa]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!pageRef.current?.contains(e.target)) {
        setFiltroSugVisible(false);
        setModalSugVisible(false);
      }
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  const filtradas = useMemo(() => {
    return filtrarImpostosClient(linhas, {
      cnpj: filtroEmpresaSel?.cnpj || "",
      qEmpresa: filtroEmpresaSel ? "" : filtroEmpresaTexto,
      refIni: filtroRefIni,
      refFim: filtroRefFim,
      cprb: filtroCprb,
    });
  }, [
    linhas,
    filtroEmpresaSel,
    filtroEmpresaTexto,
    filtroRefIni,
    filtroRefFim,
    filtroCprb,
  ]);

  const listHint = useMemo(() => {
    const n = filtradas.length;
    const total = linhas.length;
    let text = `${n} registro${n !== 1 ? "s" : ""}`;
    if (n !== total) text += ` (filtrado de ${total})`;
    return text;
  }, [filtradas.length, linhas.length]);

  const limparFiltros = () => {
    setFiltroEmpresaTexto("");
    setFiltroEmpresaSel(null);
    setFiltroSugestoes([]);
    setFiltroSugVisible(false);
    setFiltroRefIni("");
    setFiltroRefFim("");
    setFiltroCprb("");
  };

  const limparModal = () => {
    setEmpresaModal(null);
    setEditId(null);
    setModalBuscaEmpresa("");
    setModalSugestoes([]);
    setModalSugVisible(false);
    setPeriodoIni("");
    setPeriodoFim("");
    setReferenciaEdit("");
    setTerceiros(formatarPctInput(taxa20sPadrao));
    setRat(formatarPctInput(DEFAULTS.rat));
    setCprb(DEFAULTS.cprb);
    setInss(formatarPctInput(DEFAULTS.inss));
    setModalErro("");
  };

  const abrirNovo = () => {
    setModalMode("create");
    limparModal();
    setModalOpen(true);
  };

  const abrirEditar = (r) => {
    setModalMode("edit");
    limparModal();
    setEditId(r.idImposto);
    setEmpresaModal({
      cnpj: r.cnpj,
      nome: r.razaoSocial,
      idEmpresa: null,
    });
    setModalBuscaEmpresa(r.razaoSocial);
    setReferenciaEdit(r.referenciaDisplay || isoParaMmAaaa(r.referencia));
    setTerceiros(formatarPctInput(r.terceiros));
    setRat(formatarPctInput(r.rat));
    setCprb(r.cprb);
    setInss(formatarPctInput(r.inss));
    setModalOpen(true);
  };

  const fecharModal = () => {
    setModalOpen(false);
    setModalErro("");
  };

  const salvarModal = async () => {
    setModalErro("");
    if (!empresaModal?.cnpj) {
      setModalErro("Selecione uma empresa.");
      return;
    }
    const aliquotas = validarAliquotas({
      terceiros,
      rat,
      inss,
    });
    if (!aliquotas) {
      setModalErro(
        "Informe Terceiros, RAT e Imposto INSS em percentual válido."
      );
      return;
    }

    setSaving(true);
    try {
      if (modalMode === "create") {
        const iniIso = mmAaaaParaIso(periodoIni);
        const fimIso = mmAaaaParaIso(periodoFim);
        if (!iniIso || !fimIso) {
          setModalErro("Informe o período inicial e final (mm/aaaa).");
          setSaving(false);
          return;
        }
        const response = await cadastrarImpostosEsocial(
          { cnpj: empresaModal.cnpj },
          {
            impostos: {
              referenciaInicio: iniIso,
              referenciaFim: fimIso,
              terceiros: aliquotas.terceiros,
              rat: aliquotas.rat,
              inss: aliquotas.inss,
              cprb,
            },
          }
        );
        const criados = response?.criados ?? 0;
        setMsg(
          criados > 0
            ? `${criados} linha${criados !== 1 ? "s" : ""} criada${criados !== 1 ? "s" : ""}.`
            : response?.mensagem || "Cadastro realizado com sucesso!"
        );
        setOpenSnackSucesso(true);
        fecharModal();
        await carregarImpostos();
      } else {
        const refIso = mmAaaaParaIso(referenciaEdit);
        if (!refIso) {
          setModalErro("Referência inválida. Use o formato mm/aaaa.");
          setSaving(false);
          return;
        }
        await atualizarImpostosEsocial(editId, {
          impostos: {
            referencia: refIso,
            terceiros: aliquotas.terceiros,
            rat: aliquotas.rat,
            inss: aliquotas.inss,
            cprb,
          },
        });
        setMsg("Registro atualizado.");
        setOpenSnackSucesso(true);
        fecharModal();
        await carregarImpostos();
      }
    } catch (error) {
      setModalErro(
        error.response?.data?.mensagem ||
          (modalMode === "create"
            ? "Erro ao cadastrar."
            : "Erro ao atualizar.")
      );
    } finally {
      setSaving(false);
    }
  };

  const confirmarExcluir = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await excluirImpostosEsocial(deleteTarget.idImposto);
      setMsg("Registro excluído.");
      setOpenSnackSucesso(true);
      setDeleteOpen(false);
      setDeleteTarget(null);
      await carregarImpostos();
    } catch (error) {
      setMsg(error.response?.data?.mensagem || "Erro ao excluir.");
      setOpenSnackErro(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.page} ref={pageRef}>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>
            <SavingsIcon className={styles.pageTitleIcon} aria-hidden />
            Impostos INSS
          </h1>
          <p className={styles.pageSubtitle}>
            Alíquotas e flags por empresa e competência (mm/aaaa). Ao cadastrar
            um período, o sistema gera uma linha por mês.
          </p>
        </div>
        <button type="button" className={styles.btnPrimary} onClick={abrirNovo}>
          <AddIcon fontSize="small" />
          Novo registro
        </button>
      </div>

      <div className={styles.filtersPanel}>
        <div className={styles.filtersTitle}>Filtros</div>
        <div className={styles.filtersGrid}>
          <div className={styles.formField}>
            <label htmlFor="filtroEmpresa">Empresa</label>
            <div className={styles.empresaSearchWrap}>
              <input
                id="filtroEmpresa"
                type="search"
                placeholder="CNPJ ou razão social"
                autoComplete="off"
                value={filtroEmpresaTexto}
                onChange={(e) => {
                  setFiltroEmpresaSel(null);
                  setFiltroEmpresaTexto(e.target.value);
                  debouncedFiltroEmpresa(e.target.value);
                }}
              />
              <div
                className={`${styles.empresaSugestoes} ${
                  filtroSugVisible ? styles.empresaSugestoesVisible : ""
                }`}
                role="listbox"
              >
                {filtroSugestoes.map((e) => (
                  <div
                    key={e.cnpj}
                    className={styles.empresaSugestaoItem}
                    role="option"
                    onMouseDown={(ev) => {
                      ev.preventDefault();
                      setFiltroEmpresaSel(e);
                      setFiltroEmpresaTexto(e.nome);
                      setFiltroSugVisible(false);
                    }}
                  >
                    <div className={styles.empresaSugestaoNome}>{e.nome}</div>
                    <div className={styles.empresaSugestaoCnpj}>
                      {formatarCnpj(e.cnpj)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className={styles.formField}>
            <label>Referência</label>
            <div className={styles.refRange}>
              <input
                type="text"
                placeholder="mm/aaaa"
                maxLength={7}
                aria-label="Referência inicial"
                value={filtroRefIni}
                onChange={(e) => setFiltroRefIni(mascararMmAaaa(e.target.value))}
              />
              <span>a</span>
              <input
                type="text"
                placeholder="mm/aaaa"
                maxLength={7}
                aria-label="Referência final"
                value={filtroRefFim}
                onChange={(e) => setFiltroRefFim(mascararMmAaaa(e.target.value))}
              />
            </div>
          </div>
          <div className={styles.formField}>
            <label htmlFor="filtroCprb">CPRB</label>
            <select
              id="filtroCprb"
              value={filtroCprb}
              onChange={(e) => setFiltroCprb(e.target.value)}
            >
              <option value="">Todos</option>
              <option value="Sim">Sim</option>
              <option value="Não">Não</option>
            </select>
          </div>
          <button
            type="button"
            className={styles.btnFilter}
            onClick={limparFiltros}
          >
            Limpar
          </button>
        </div>
      </div>

      <p className={styles.listHint}>{listHint}</p>

      {loading ? (
        <div className={styles.loadingMsg}>
          <CircularProgress size={28} sx={{ mr: 1 }} />
          Carregando…
        </div>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th scope="col">CNPJ</th>
                <th scope="col">Razão Social</th>
                <th scope="col">Referência</th>
                <th className={styles.num} scope="col">
                  Terceiros
                </th>
                <th className={styles.num} scope="col">
                  RAT
                </th>
                <th className={styles.num} scope="col">
                  CPRB
                </th>
                <th className={styles.num} scope="col">
                  Imposto INSS
                </th>
                <th scope="col">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtradas.map((r) => (
                <tr key={r.idImposto}>
                  <td>{formatarCnpj(r.cnpj)}</td>
                  <td>{r.razaoSocial || "—"}</td>
                  <td>
                    <strong>{r.referenciaDisplay}</strong>
                  </td>
                  <td className={styles.num}>{formatarPct(r.terceiros)}</td>
                  <td className={styles.num}>{formatarPct(r.rat)}</td>
                  <td className={styles.num}>
                    <span
                      className={`${styles.badgeCprb} ${
                        r.cprb === "Sim" ? styles.badgeSim : styles.badgeNao
                      }`}
                    >
                      {r.cprb}
                    </span>
                  </td>
                  <td className={styles.num}>{formatarPct(r.inss)}</td>
                  <td>
                    <div className={styles.acoesCell}>
                      <button
                        type="button"
                        className={styles.btnIcon}
                        title="Editar"
                        onClick={() => abrirEditar(r)}
                      >
                        <EditIcon fontSize="small" />
                      </button>
                      <button
                        type="button"
                        className={`${styles.btnIcon} ${styles.btnIconDanger}`}
                        title="Excluir"
                        onClick={() => {
                          setDeleteTarget(r);
                          setDeleteOpen(true);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!filtradas.length ? (
            <p className={styles.emptyMsg}>Nenhum registro encontrado.</p>
          ) : null}
        </div>
      )}

      <Dialog open={modalOpen} onClose={fecharModal} fullWidth maxWidth="sm">
        <DialogTitle>
          {modalMode === "create" ? "Novo registro" : "Editar registro"}
        </DialogTitle>
        <DialogContent>
          <div className={styles.modalGrid} style={{ marginTop: 8 }}>
            <div className={`${styles.formField} ${styles.span2}`}>
              <label htmlFor="modalBuscaEmpresa">Empresa</label>
              <div className={styles.empresaSearchWrap}>
                <input
                  id="modalBuscaEmpresa"
                  type="search"
                  placeholder="CNPJ ou razão social"
                  autoComplete="off"
                  disabled={modalMode === "edit"}
                  value={modalBuscaEmpresa}
                  onChange={(e) => {
                    if (modalMode === "edit") return;
                    setEmpresaModal(null);
                    setModalBuscaEmpresa(e.target.value);
                    debouncedModalEmpresa(e.target.value);
                  }}
                />
                {modalMode === "create" ? (
                  <div
                    className={`${styles.empresaSugestoes} ${
                      modalSugVisible ? styles.empresaSugestoesVisible : ""
                    }`}
                    role="listbox"
                  >
                    {modalSugestoes.map((e) => (
                      <div
                        key={e.cnpj}
                        className={styles.empresaSugestaoItem}
                        role="option"
                        onMouseDown={(ev) => {
                          ev.preventDefault();
                          setEmpresaModal(e);
                          setModalBuscaEmpresa(e.nome);
                          setModalSugVisible(false);
                        }}
                      >
                        <div className={styles.empresaSugestaoNome}>
                          {e.nome}
                        </div>
                        <div className={styles.empresaSugestaoCnpj}>
                          {formatarCnpj(e.cnpj)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
              {modalMode === "create" ? (
                <p className={styles.modalHint}>
                  Selecione a empresa na lista de sugestões.
                </p>
              ) : null}
              {empresaModal ? (
                <div className={styles.empresaSelecionada}>
                  <strong>{empresaModal.nome}</strong>
                  <span>{formatarCnpj(empresaModal.cnpj)}</span>
                </div>
              ) : null}
            </div>

            {modalMode === "create" ? (
              <div className={`${styles.formField} ${styles.span2}`}>
                <label>Período</label>
                <div className={styles.refRange}>
                  <input
                    type="text"
                    placeholder="Inicial (mm/aaaa)"
                    maxLength={7}
                    value={periodoIni}
                    onChange={(e) =>
                      setPeriodoIni(mascararMmAaaa(e.target.value))
                    }
                  />
                  <span>a</span>
                  <input
                    type="text"
                    placeholder="Final (mm/aaaa)"
                    maxLength={7}
                    value={periodoFim}
                    onChange={(e) =>
                      setPeriodoFim(mascararMmAaaa(e.target.value))
                    }
                  />
                </div>
                <p className={styles.modalHint}>
                  Será criada uma linha por mês no intervalo, com os mesmos
                  valores dos demais campos.
                </p>
              </div>
            ) : (
              <div className={styles.formField}>
                <label htmlFor="modalReferencia">Referência</label>
                <input
                  id="modalReferencia"
                  type="text"
                  placeholder="mm/aaaa"
                  maxLength={7}
                  value={referenciaEdit}
                  onChange={(e) =>
                    setReferenciaEdit(mascararMmAaaa(e.target.value))
                  }
                />
              </div>
            )}

            <div className={styles.formField}>
              <label htmlFor="modalTerceiros">Terceiros (%)</label>
              <input
                id="modalTerceiros"
                type="text"
                inputMode="decimal"
                placeholder="Ex.: 5,80"
                value={terceiros}
                onChange={(e) => setTerceiros(e.target.value)}
              />
            </div>
            <div className={styles.formField}>
              <label htmlFor="modalRat">RAT (%)</label>
              <input
                id="modalRat"
                type="text"
                inputMode="decimal"
                placeholder="Ex.: 2,00"
                value={rat}
                onChange={(e) => setRat(e.target.value)}
              />
            </div>
            <div className={styles.formField}>
              <label htmlFor="modalCprb">CPRB</label>
              <select
                id="modalCprb"
                value={cprb}
                onChange={(e) => setCprb(e.target.value)}
              >
                <option value="Não">Não</option>
                <option value="Sim">Sim</option>
              </select>
            </div>
            <div className={styles.formField}>
              <label htmlFor="modalImpostoInss">Imposto INSS (%)</label>
              <input
                id="modalImpostoInss"
                type="text"
                inputMode="decimal"
                placeholder="Ex.: 20,00"
                value={inss}
                onChange={(e) => setInss(e.target.value)}
              />
            </div>
          </div>
          {modalErro ? <p className={styles.formError}>{modalErro}</p> : null}
          <div className={styles.modalFooter}>
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={fecharModal}
              disabled={saving}
            >
              Cancelar
            </button>
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={salvarModal}
              disabled={saving}
            >
              {saving ? "Salvando…" : "Salvar"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent>
          <p className={styles.deleteText}>
            {deleteTarget
              ? `Excluir a parametrização de ${deleteTarget.razaoSocial || "empresa"} para ${deleteTarget.referenciaDisplay}?`
              : "Excluir este registro?"}
          </p>
          <div className={styles.modalFooter}>
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={() => setDeleteOpen(false)}
              disabled={saving}
            >
              Cancelar
            </button>
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={confirmarExcluir}
              disabled={saving}
            >
              {saving ? "Excluindo…" : "Excluir"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <MensagemSucesso
        open={openSnackSucesso}
        msg={msg}
        handleClose={(_, reason) => {
          if (reason === "clickaway") return;
          setOpenSnackSucesso(false);
        }}
      />
      <MensagemErro
        open={openSnackErro}
        msg={msg}
        handleClose={(_, reason) => {
          if (reason === "clickaway") return;
          setOpenSnackErro(false);
        }}
      />
    </div>
  );
};

export default Impostos;
